<template>
  <Teleport :to="to">
    <div class="absolute inset-0 z-[1051] overflow-y-auto" v-if="showModal" :id="id">
      <div
        :class="['fixed inset-0 transition-opacity', backgroundDropColor]"
        @click="onBackdropClick"
        aria-hidden="true"
      ></div>
      <div
        class="fixed inset-0 modal block pointer-events-none"
        role="dialog"
        :aria-modal="true"
        :aria-labelledby="titleId"
      >
        <div :class="['flex min-h-full justify-center p-4 text-center  sm:p-0', modalClass]">
          <div
            :class="[
              'relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full h-fit pointer-events-auto',
              sizeClass,
              modalContentClass,
              topDownAnimation ? 'modal-content-animation' : '',
            ]"
          >
            <!-- Header -->
            <div v-if="!hideTitle" :class="['modal-header w-full', titleClass]">
              <slot name="modal-title"></slot>
              <button
                v-if="!hideCloseButton"
                type="button"
                class="text-[#afb8bc] hover:text-[#607179]"
                @click="hide"
                :aria-label="i18n.global.t('accessibility.closeModal')"
              >
                <span class="sr-only">{{ i18n.global.t('common.close') }}</span>
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div :class="['modal-body p-[20px] w-full', bodyClass]">
              <slot></slot>
            </div>

            <!-- Footer -->
            <div
              v-if="!hideFooter"
              class="modal-footer bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse w-full rounded-b-lg"
            >
              <slot name="modal-footer"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { i18n } from '@/localization'

export default defineComponent({
  name: 'UIModal',
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    hideCloseButton: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator: (value: string) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value),
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    preventClose: {
      type: Boolean,
      default: false,
    },
    closeButtonTooltip: {
      type: [String, Object],
      default: 'common.buttons.close',
    },
    titleClass: {
      type: String,
      default: '',
    },
    bodyClass: {
      type: String,
      default: '',
    },
    modalClass: {
      type: [String, Array],
      default: '',
    },
    modalContentClass: {
      type: String,
      default: '',
    },
    to: {
      type: String,
      default: 'body',
    },
    backgroundDropColor: {
      type: String,
      default: 'bg-[#2a3135cc]',
    },
    duration: {
      type: Number,
      default: 300,
    },
    topDownAnimation: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: 'ui-modal',
    },
  },
  emits: ['update:showModal', 'hide', 'show'],
  data() {
    return {
      titleId: `modal-title-${Math.random().toString(36).substr(2, 9)}`,
      i18n,
    }
  },
  computed: {
    sizeClass(): string {
      const sizes = {
        sm: 'sm:max-w-[680px]',
        md: 'sm:max-w-[800px]',
        lg: 'sm:max-w-[1024px]',
        xl: 'sm:max-w-[1280px]',
        full: 'sm:max-w-full sm:m-4',
      }
      return sizes[this.size as keyof typeof sizes] || sizes.md
    },
    enterActiveClass() {
      return `ease-out duration-${this.duration}`
    },
    leaveActiveClass() {
      return `ease-in duration-${Math.floor(this.duration * 0.67)}`
    },
  },
  methods: {
    show() {
      if (this.preventClose) return
      this.$emit('update:showModal', true)
      this.$emit('show')
    },
    hide() {
      console.log('hide' + this.preventClose)
      if (this.preventClose) return
      this.$emit('update:showModal', false)
      this.$emit('hide')
    },
    onBackdropClick() {
      console.log('onBackdropClick' + this.closeOnBackdrop)
      if (this.closeOnBackdrop) {
        this.hide()
      }
    },
    handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape' && this.showModal && !this.preventClose) {
        this.hide()
      }
    },
  },
  mounted() {
    console.log('UIModal mounted' + this.showModal)
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },
})
</script>

<style scoped>
/* Additional styling to match bootstrap modal */
.modal-header {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dee2e6;
  padding: 25px 15px;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
}

.modal-title {
  margin: 0;
  line-height: 1.5;
  font-size: 1rem;
}

.modal-content-animation {
  animation: slide-up 0.3s ease-out;
}

@keyframes slide-up {
  0% {
    transform: translateY(-25%);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
